import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { endOfMonth, format, isBefore, startOfDay, startOfMonth, subMonths } from "date-fns";
import { ArrowLeft, Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/components/shared/Pagination";
import {
  money, signedMoney, fmtDate, ENTRY_LABEL, statusLabel, STATUS_STYLE, CHANNEL_LABEL, CHANNEL_STYLE,
} from "@/components/payouts/ledgerDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = { reseller: "Reseller", distributor: "Distributor" };

const thisMonth = () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) });
const lastMonth = () => {
  const d = subMonths(new Date(), 1);
  return { from: startOfMonth(d), to: endOfMonth(d) };
};

// Local-day bounds sent as full ISO instants — the same convention as AdminEarnings, so a
// month means the admin's own calendar month whatever timezone the server runs in.
const dayStartIso = (d) => { const s = new Date(d); s.setHours(0, 0, 0, 0); return s.toISOString(); };
const dayEndIso = (d) => { const e = new Date(d); e.setHours(23, 59, 59, 999); return e.toISOString(); };

const PRESETS = [
  { key: "this", label: "This month", range: thisMonth },
  { key: "last", label: "Last month", range: lastMonth },
  { key: "all", label: "All time", range: () => null },
];

/**
 * One reseller's or distributor's whole ledger — credits (earned from orders) and debits
 * (payouts) — for a date range that defaults to the current month. Reached from the
 * "Ledger" button on the admin Payouts page.
 */
export default function AdminOwnerLedger() {
  const { kind, ownerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const topRef = useRef(null);
  const kindValid = Object.prototype.hasOwnProperty.call(KINDS, kind);

  // `range` is what has been applied (null = all time). `draft` is what the calendar shows
  // while a range is being picked; nothing is applied until both ends are chosen, because a
  // lone start date would query an open-ended range.
  const [range, setRange] = useState(thisMonth);
  const [draft, setDraft] = useState(undefined);
  const [preset, setPreset] = useState("this");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const startDate = range ? dayStartIso(range.from) : null;
  const endDate = range ? dayEndIso(range.to) : null;

  // The admin layout scrolls one shared container, so a page opened from the middle of a
  // long list would otherwise start scrolled.
  useEffect(() => { topRef.current?.scrollIntoView?.({ block: "start" }); }, []);

  useEffect(() => {
    if (!kindValid) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = { page, limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    axios
      .get(`${API}/admin/ledger/${kind}/${encodeURIComponent(ownerId)}`, { params })
      .then(({ data: res }) => { if (!cancelled) setData(res); })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(err.response?.status === 404 ? "notfound" : err.response?.data?.message || "Couldn't load this ledger.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, kindValid, ownerId, startDate, endDate, page, limit]);

  const applyRange = (next, key) => {
    setRange(next);
    setPreset(key);
    setPage(1);
  };

  // Two clicks make a range: the first starts one, the second finishes it (in either order,
  // and the same day twice is a one-day range). react-day-picker's own range logic would
  // instead EXTEND the range that is already showing, so the first click would apply at once.
  const onPick = (_range, day) => {
    if (!day) return;
    if (!draft?.from || draft.to) {
      setDraft({ from: day, to: undefined });
      return;
    }
    const backwards = isBefore(startOfDay(day), startOfDay(draft.from));
    const next = backwards ? { from: day, to: draft.from } : { from: draft.from, to: day };
    setDraft(next);
    applyRange(next, "custom");
    setPickerOpen(false);
  };

  const goBack = () =>
    navigate(location.state?.from || `/admin/payouts?tab=${kind === "distributor" ? "distributors" : "resellers"}`);

  const rangeLabel = useMemo(
    () => (range ? `${format(range.from, "LLL dd, y")} – ${format(range.to, "LLL dd, y")}` : "All time"),
    [range]
  );

  const backButton = (
    <Button variant="outline" size="sm" onClick={goBack} data-testid="ledger-back">
      <ArrowLeft className="w-4 h-4 mr-1" /> Back to Payouts
    </Button>
  );

  if (!kindValid || error === "notfound") {
    return (
      <div ref={topRef} className="space-y-4" data-testid="owner-ledger-notfound">
        {backButton}
        <div className="py-16 text-center text-slate-500">Ledger not found.</div>
      </div>
    );
  }

  const owner = data?.owner;
  const totals = data?.totals;
  const pagination = data?.pagination;

  return (
    <div ref={topRef} className="space-y-6" data-testid="owner-ledger">
      <div className="space-y-3">
        {backButton}
        <div>
          <h2 className="text-2xl font-bold text-slate-900">{owner?.name || "Ledger"}</h2>
          <p className="text-slate-500">
            {KINDS[kind]} ledger
            {owner && (owner.subdomain || owner.email) ? ` · ${owner.subdomain || owner.email}` : ""}
          </p>
        </div>
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] text-slate-400 font-bold uppercase ml-1">Date range</label>
          <Popover
            open={pickerOpen}
            onOpenChange={(o) => { setPickerOpen(o); if (o) setDraft(range || undefined); }}
          >
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[280px] justify-start text-left font-normal bg-white" data-testid="ledger-range">
                <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={range?.from}
                selected={draft}
                onSelect={onPick}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={preset === p.key ? "default" : "outline"}
              className={preset === p.key ? "bg-amber-600 hover:bg-amber-700" : "bg-white"}
              onClick={() => applyRange(p.range(), p.key)}
              data-testid={`ledger-preset-${p.key}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>

      {loading && !data && (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-amber-600 animate-spin" /></div>
      )}

      {!loading && error && (
        <div className="py-12 text-center text-red-600" data-testid="owner-ledger-error">{error}</div>
      )}

      {data && (
        <div className={loading ? "space-y-6 opacity-60 transition-opacity" : "space-y-6"}>
          {/* Totals for the whole range, not just the page shown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <p className="text-sm text-slate-500">Credits</p>
              <p className="text-2xl font-bold text-green-600" data-testid="ledger-credits">+{money(totals.credits)}</p>
              <p className="text-xs text-slate-400">Earned from orders</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <p className="text-sm text-slate-500">Debits</p>
              <p className="text-2xl font-bold text-red-600" data-testid="ledger-debits">−{money(totals.debits)}</p>
              <p className="text-xs text-slate-400">
                {totals.debits_unpaid > 0 ? `incl. ${money(totals.debits_unpaid)} not yet paid` : "Paid out"}
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <p className="text-sm text-slate-500">Net for period</p>
              <p className={`text-2xl font-bold ${totals.net < 0 ? "text-red-600" : "text-slate-900"}`} data-testid="ledger-net">
                {signedMoney(totals.net)}
              </p>
              <p className="text-xs text-slate-400">Credits − debits</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            {data.entries.length === 0 ? (
              <div className="py-14 text-center" data-testid="owner-ledger-empty">
                <p className="text-slate-600">No ledger entries {range ? "in this period" : "yet"}.</p>
                {range && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => applyRange(null, "all")}>
                    Show all time
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Entry</TableHead>
                      <TableHead>Order #</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Payout</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.entries.map((e) => {
                      const isDebit = e.direction === "debit";
                      const struck = isDebit && e.status === "reversed";
                      return (
                        <TableRow key={e.key} className={struck ? "text-slate-400" : ""}>
                          <TableCell className="whitespace-nowrap">{fmtDate(e.created_at)}</TableCell>
                          <TableCell className="whitespace-nowrap">{ENTRY_LABEL[e.type] || e.type}</TableCell>
                          <TableCell className="font-mono text-xs">{e.order_number || "—"}</TableCell>
                          <TableCell>
                            {e.channel ? (
                              <Badge className={CHANNEL_STYLE[e.channel] || "bg-slate-100 text-slate-700"}>
                                {CHANNEL_LABEL[e.channel] || e.channel}
                              </Badge>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{e.payout_id || "—"}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {!isDebit ? `+${money(e.amount)}` : ""}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${struck ? "line-through text-slate-400" : "text-red-600"}`}>
                            {isDebit ? `−${money(e.amount)}` : ""}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_STYLE[e.status] || "bg-slate-100 text-slate-700"}>{statusLabel(e)}</Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
            {pagination && pagination.total > 0 && (
              <div className="p-4 border-t">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onChange={setPage}
                  totalItems={pagination.total}
                  pageSize={limit}
                  onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
                />
              </div>
            )}
          </div>
          <p className="text-xs text-slate-400">
            An entry's date is when it was created — for a payout, when it was raised. Reversed payouts are shown but not counted.
          </p>
        </div>
      )}
    </div>
  );
}
